import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker } from 'antd';
import { useGetActivityLogsQuery } from '../services/auth/authApi';
import Loading from '../components/Loading';

const { RangePicker } = DatePicker;

const LeftArrowIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z"></path>
  </svg>
);

const RightArrowIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
    <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z"></path>
  </svg>
);

const AccountOperationsPage = () => {
  const navigate    = useNavigate();
  const { userId }  = useParams();

  const [page, setPage]                 = useState(1);
  const [dates, setDates]               = useState([null, null]);
  const [activityType, setActivityType] = useState('');
  const [appliedDates, setAppliedDates] = useState({ startDate: '', endDate: '' });

  const { data, isLoading, error } = useGetActivityLogsQuery(
    { userId, page, limit: 20, ...appliedDates, activityType },
    { skip: !userId }
  );

  const operations = data?.data       || [];
  const pagination = data?.pagination || {};
  const totalPages = pagination.totalPages || 1;
  const totalCount = pagination.total      || operations.length;

  const handleActivityTypeChange = (e) => {
    setActivityType(e.target.value);
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
    setAppliedDates({
      startDate: dates[0] ? dates[0].format('YYYY-MM-DD') : '',
      endDate:   dates[1] ? dates[1].format('YYYY-MM-DD') : '',
    });
  };

  const handleReset = () => {
    setDates([null, null]); setActivityType('');
    setPage(1);
    setAppliedDates({ startDate: '', endDate: '' });
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between">
        <span className="text-white text-base font-semibold">
          List Of All Transactions ({totalCount})
        </span>
        <button
          onClick={() => navigate(-1)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 text-sm transition-colors"
        >
          Back
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Date Range</label>
          <RangePicker
            value={dates}
            onChange={(vals) => setDates(vals || [null, null])}
            format="DD-MM-YYYY"
            allowClear
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Activity Type</label>
          <select
            value={activityType}
            onChange={handleActivityTypeChange}
            className="border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400 rounded bg-white"
          >
            <option value="">All</option>
            <option value="deposit">Deposit</option>
            <option value="withdraw">Withdraw</option>
            <option value="login_success">Login</option>
          </select>
        </div>
        <button
          onClick={handleSearch}
          className="bg-[#0F2327] hover:bg-[#1a3a40] text-white px-5 py-1.5 text-sm transition-colors rounded"
        >
          Search
        </button>
        <button
          onClick={handleReset}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-1.5 text-sm rounded transition-colors"
        >
          Reset
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-8"><Loading /></div>
        ) : error ? (
          <div className="p-4 text-red-500 text-center">
            {error?.data?.message || 'Failed to load activity logs'}
          </div>
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[#0F2327] text-white">
                <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40] whitespace-nowrap">Date</th>
                <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Operation</th>
                <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Amount</th>
                <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Done By</th>
                <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Description</th>
              </tr>
            </thead>
            <tbody>
              {operations.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-gray-400 border border-gray-200">
                    No operations found
                  </td>
                </tr>
              ) : (
                operations.map((op, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2.5 border border-gray-200 whitespace-nowrap text-gray-700">{op.date || '-'}</td>
                    <td className="px-4 py-2.5 border border-gray-200 whitespace-nowrap font-medium text-gray-800">{op.operation || '-'}</td>
                    <td className={`px-4 py-2.5 border border-gray-200 whitespace-nowrap font-semibold ${op.amount < 0 ? 'text-red-500' : 'text-green-600'}`}>{op.amount ?? '-'}</td>
                    <td className="px-4 py-2.5 border border-gray-200 whitespace-nowrap text-gray-700">{op.doneBy || '-'}</td>
                    <td className="px-4 py-2.5 border border-gray-200 text-gray-700">{op.description || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-end p-3">
                <ul className="flex items-center gap-1">
                  <li>
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <LeftArrowIcon />
                    </button>
                  </li>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let p;
                    if (totalPages <= 5)       p = i + 1;
                    else if (page <= 3)        p = i + 1;
                    else if (page >= totalPages - 2) p = totalPages - 4 + i;
                    else                       p = page - 2 + i;
                    return (
                      <li key={p}>
                        <button
                          onClick={() => setPage(p)}
                          className={`px-3 py-1 rounded ${page === p ? 'bg-blue-500 text-white' : 'border border-gray-300 hover:bg-gray-100'}`}
                        >
                          {p}
                        </button>
                      </li>
                    );
                  })}
                  <li>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <RightArrowIcon />
                    </button>
                  </li>
                </ul>
              </div>
          )}
    </div>
  );
};

export default AccountOperationsPage;
